import { useMemo, isValidElement } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Swiper, SwiperSlide } from 'swiper/react';
import 'swiper/css';
import 'swiper/css/pagination';
import { Pagination } from 'swiper/modules';
import { Calendar, Users, MapPin, ArrowRight } from 'lucide-react';

function formatDiscoverLabel(raw: unknown): string {
  if (raw == null || raw === '') return '';
  return String(raw)
    .split(/_+/)
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
}

type DiscoverRecord = Record<string, unknown> | null;

interface DiscoverStripProps {
  loading: boolean;
  discoverEvent: DiscoverRecord;
  discoverPractitioner: DiscoverRecord;
  discoverVenue: DiscoverRecord;
}

export function DiscoverStrip({
  loading,
  discoverEvent,
  discoverPractitioner,
  discoverVenue,
}: DiscoverStripProps) {
  const { t } = useTranslation();

  const discoverCards = useMemo(() => {
    const liveStream = (
      <Link
        key="discover-live-stream"
        to="/live-stream"
        className="group block h-full transition-all duration-150 cursor-pointer active:scale-[0.98]"
      >
        <div className="relative rounded-2xl overflow-hidden">
          <video
            autoPlay
            loop
            muted
            playsInline
            className="w-full h-48 object-cover group-hover:scale-[1.02] transition-transform duration-300"
          >
            <source
              src="https://afvltpqnhmaxanirwnqz.supabase.co/storage/v1/object/public/post-images/59bed50f-5ccf-4265-87fa-7743af34d361/live-stream-woman.mp4"
              type="video/mp4"
            />
          </video>
          <div className="absolute inset-0 bg-black/20 pointer-events-none" />
          <span
            className="absolute top-3 right-3 px-2 py-1 text-xs font-medium text-white rounded-full"
            style={{ backgroundColor: '#C17F59' }}
          >
            COMING SOON
          </span>
        </div>
        <div className="pt-4 flex flex-col flex-1">
          <div className="flex flex-wrap gap-2 mb-3">
            <span
              className="inline-flex px-2 py-1 text-xs font-medium text-white rounded-full"
              style={{ backgroundColor: '#C17F59' }}
            >
              {t('home.discoverLiveStreamTitle')}
            </span>
          </div>
          <h3 className="text-lg font-semibold mb-2 text-content group-hover:text-accent-text transition-colors line-clamp-2">
            {t('home.liveSessionsTitle')}
          </h3>
          <div className="space-y-2 text-sm text-content/80">
            <p>{t('home.discoverLiveStreamDesc')}</p>
          </div>
          <span className="mt-4 inline-flex items-center text-sm font-medium text-accent-text">
            {t('home.joinLiveSessions')}
            <ArrowRight className="w-4 h-4 ml-1.5 group-hover:translate-x-0.5 transition-transform" />
          </span>
        </div>
      </Link>
    );

    const eventCard =
      discoverEvent && typeof discoverEvent.slug === 'string' ? (
        <Link
          key="discover-event"
          to={`/events/${discoverEvent.slug}`}
          className="group block h-full transition-all duration-150 cursor-pointer active:scale-[0.98]"
        >
          <div className="relative rounded-2xl overflow-hidden">
            {discoverEvent.image_url ? (
              <img
                src={String(discoverEvent.image_url)}
                alt={String(discoverEvent.title ?? '')}
                className="w-full h-48 object-cover group-hover:scale-[1.02] transition-transform duration-300"
                loading="lazy"
              />
            ) : (
              <div
                className="w-full h-48 flex items-center justify-center"
                style={{ backgroundColor: '#F4F7EE' }}
              >
                <span className="text-2xl font-semibold text-accent-text font-gelica">
                  {t('home.discoverLabelEvent')}
                </span>
              </div>
            )}
          </div>
          <div className="pt-4 flex flex-col flex-1">
            <div className="flex flex-wrap gap-2 mb-3">
              <span
                className="inline-flex px-2 py-1 text-xs font-medium text-white rounded-full"
                style={{ backgroundColor: '#C17F59' }}
              >
                {t('home.discoverLabelEvent')}
              </span>
            </div>
            <h3 className="text-lg font-semibold mb-2 text-content line-clamp-2 group-hover:text-accent-text transition-colors">
              {String(discoverEvent.title ?? '')}
            </h3>
            <div className="space-y-2 text-sm text-content/80 mb-4">
              <div className="flex items-center">
                <img
                  src="https://afvltpqnhmaxanirwnqz.supabase.co/storage/v1/object/public/icons/calendar-icon.png"
                  alt=""
                  className="h-4 w-4 mr-2 shrink-0"
                />
                <span>
                  {discoverEvent.start_date
                    ? new Date(String(discoverEvent.start_date)).toLocaleDateString(undefined, {
                        weekday: 'short',
                        month: 'short',
                        day: 'numeric',
                        year: 'numeric',
                      })
                    : '—'}
                </span>
              </div>
              {discoverEvent.location ? (
                <div className="flex items-start">
                  <img
                    src="https://afvltpqnhmaxanirwnqz.supabase.co/storage/v1/object/public/icons/location-icon.png"
                    alt=""
                    className="h-4 w-4 mr-2 mt-0.5 shrink-0"
                  />
                  <span className="line-clamp-2">{String(discoverEvent.location)}</span>
                </div>
              ) : null}
            </div>
            <span className="mt-auto inline-flex items-center text-sm font-medium text-accent-text">
              {t('home.forYouDiscoverEvents')}
              <ArrowRight className="w-4 h-4 ml-1.5 group-hover:translate-x-0.5 transition-transform" />
            </span>
          </div>
        </Link>
      ) : (
        <Link
          key="discover-event-empty"
          to="/events"
          className="group block h-full transition-all duration-150 cursor-pointer active:scale-[0.98] rounded-2xl border border-dashed border-gray-200 p-4 hover:border-accent-text/30 hover:bg-accent-text/5"
        >
          <div
            className="rounded-2xl overflow-hidden flex items-center justify-center h-48"
            style={{ backgroundColor: '#F4F7EE' }}
          >
            <Calendar className="w-12 h-12 text-accent-text/40" aria-hidden />
          </div>
          <div className="pt-4 text-center">
            <span
              className="inline-flex px-2 py-1 text-xs font-medium text-white rounded-full"
              style={{ backgroundColor: '#C17F59' }}
            >
              {t('home.discoverLabelEvent')}
            </span>
            <p className="mt-3 text-sm font-medium text-content">
              {t('home.discoverBrowseCategory', { category: t('home.forYouDiscoverEvents') })}
            </p>
          </div>
        </Link>
      );

    const practitionerCard =
      discoverPractitioner && typeof discoverPractitioner.slug === 'string' ? (
        <Link
          key="discover-practitioner"
          to={`/practitioners/${discoverPractitioner.slug}`}
          className="group block h-full transition-all duration-150 cursor-pointer active:scale-[0.98]"
        >
          <div className="relative rounded-2xl overflow-hidden">
            {(() => {
              const imgs = discoverPractitioner.images as string[] | undefined;
              const user = discoverPractitioner.user as { avatar_url?: string } | undefined;
              const src = imgs?.[0] || user?.avatar_url;
              return src ? (
                <img
                  src={src}
                  alt={String(discoverPractitioner.title ?? '')}
                  className="w-full h-48 object-cover group-hover:scale-[1.02] transition-transform duration-300"
                  loading="lazy"
                />
              ) : (
                <div
                  className="w-full h-48 flex items-center justify-center"
                  style={{ backgroundColor: '#F4F7EE' }}
                >
                  <span className="text-2xl font-semibold text-accent-text font-gelica">
                    {t('home.discoverLabelPractitioner')}
                  </span>
                </div>
              );
            })()}
          </div>
          <div className="pt-4 flex flex-col flex-1">
            <div className="flex flex-wrap gap-2 mb-3">
              <span
                className="inline-flex px-2 py-1 text-xs font-medium text-white rounded-full"
                style={{ backgroundColor: '#C17F59' }}
              >
                {t('home.discoverLabelPractitioner')}
              </span>
              {discoverPractitioner.category ? (
                <span className="inline-flex px-2 py-1 text-xs font-medium bg-gray-100 text-gray-700 rounded-full truncate max-w-full">
                  {formatDiscoverLabel(discoverPractitioner.category)}
                </span>
              ) : null}
            </div>
            <h3 className="text-lg font-semibold mb-2 text-content line-clamp-2 group-hover:text-accent-text transition-colors">
              {String(discoverPractitioner.title ?? '')}
            </h3>
            <div className="space-y-2 text-sm text-content/80 mb-4">
              <div className="flex items-start">
                <img
                  src="https://afvltpqnhmaxanirwnqz.supabase.co/storage/v1/object/public/icons/location-icon.png"
                  alt=""
                  className="h-4 w-4 mr-2 mt-0.5 shrink-0"
                />
                <span className="line-clamp-2">
                  {[discoverPractitioner.address, discoverPractitioner.country].filter(Boolean).join(', ') ||
                    '—'}
                </span>
              </div>
            </div>
            <span className="mt-auto inline-flex items-center text-sm font-medium text-accent-text">
              {t('home.forYouDiscoverPractitioners')}
              <ArrowRight className="w-4 h-4 ml-1.5 group-hover:translate-x-0.5 transition-transform" />
            </span>
          </div>
        </Link>
      ) : (
        <Link
          key="discover-practitioner-empty"
          to="/practitioners"
          className="group block h-full transition-all duration-150 cursor-pointer active:scale-[0.98] rounded-2xl border border-dashed border-gray-200 p-4 hover:border-accent-text/30 hover:bg-accent-text/5"
        >
          <div
            className="rounded-2xl overflow-hidden flex items-center justify-center h-48"
            style={{ backgroundColor: '#F4F7EE' }}
          >
            <Users className="w-12 h-12 text-accent-text/40" aria-hidden />
          </div>
          <div className="pt-4 text-center">
            <span
              className="inline-flex px-2 py-1 text-xs font-medium text-white rounded-full"
              style={{ backgroundColor: '#C17F59' }}
            >
              {t('home.discoverLabelPractitioner')}
            </span>
            <p className="mt-3 text-sm font-medium text-content">
              {t('home.discoverBrowseCategory', { category: t('home.forYouDiscoverPractitioners') })}
            </p>
          </div>
        </Link>
      );

    const venueCard =
      discoverVenue && typeof discoverVenue.slug === 'string' ? (
        <Link
          key="discover-venue"
          to={`/venues/${discoverVenue.slug}`}
          className="group block h-full transition-all duration-150 cursor-pointer active:scale-[0.98]"
        >
          <div className="relative rounded-2xl overflow-hidden">
            {(() => {
              const imgs = discoverVenue.images as string[] | undefined;
              const src = imgs?.[0];
              return src ? (
                <img
                  src={src}
                  alt={String(discoverVenue.name ?? '')}
                  className="w-full h-48 object-cover group-hover:scale-[1.02] transition-transform duration-300"
                  loading="lazy"
                />
              ) : (
                <div
                  className="w-full h-48 flex items-center justify-center"
                  style={{ backgroundColor: '#F4F7EE' }}
                >
                  <span className="text-2xl font-semibold text-accent-text font-gelica">
                    {t('home.discoverLabelVenue')}
                  </span>
                </div>
              );
            })()}
          </div>
          <div className="pt-4 flex flex-col flex-1">
            <div className="flex flex-wrap gap-2 mb-3">
              <span
                className="inline-flex px-2 py-1 text-xs font-medium text-white rounded-full"
                style={{ backgroundColor: '#C17F59' }}
              >
                {t('home.discoverLabelVenue')}
              </span>
              {discoverVenue.venue_type ? (
                <span className="inline-flex px-2 py-1 text-xs font-medium bg-gray-100 text-gray-700 rounded-full truncate max-w-full">
                  {formatDiscoverLabel(discoverVenue.venue_type)}
                </span>
              ) : null}
            </div>
            <h3 className="text-lg font-semibold mb-2 text-content line-clamp-2 group-hover:text-accent-text transition-colors">
              {String(discoverVenue.name ?? '')}
            </h3>
            <div className="space-y-2 text-sm text-content/80 mb-4">
              <div className="flex items-start">
                <img
                  src="https://afvltpqnhmaxanirwnqz.supabase.co/storage/v1/object/public/icons/location-icon.png"
                  alt=""
                  className="h-4 w-4 mr-2 mt-0.5 shrink-0"
                />
                <span className="line-clamp-2">
                  {[discoverVenue.address, discoverVenue.country].filter(Boolean).join(', ') || '—'}
                </span>
              </div>
            </div>
            <span className="mt-auto inline-flex items-center text-sm font-medium text-accent-text">
              {t('home.forYouDiscoverVenues')}
              <ArrowRight className="w-4 h-4 ml-1.5 group-hover:translate-x-0.5 transition-transform" />
            </span>
          </div>
        </Link>
      ) : (
        <Link
          key="discover-venue-empty"
          to="/venues"
          className="group block h-full transition-all duration-150 cursor-pointer active:scale-[0.98] rounded-2xl border border-dashed border-gray-200 p-4 hover:border-accent-text/30 hover:bg-accent-text/5"
        >
          <div
            className="rounded-2xl overflow-hidden flex items-center justify-center h-48"
            style={{ backgroundColor: '#F4F7EE' }}
          >
            <MapPin className="w-12 h-12 text-accent-text/40" aria-hidden />
          </div>
          <div className="pt-4 text-center">
            <span
              className="inline-flex px-2 py-1 text-xs font-medium text-white rounded-full"
              style={{ backgroundColor: '#C17F59' }}
            >
              {t('home.discoverLabelVenue')}
            </span>
            <p className="mt-3 text-sm font-medium text-content">
              {t('home.discoverBrowseCategory', { category: t('home.forYouDiscoverVenues') })}
            </p>
          </div>
        </Link>
      );

    return [liveStream, eventCard, practitionerCard, venueCard];
  }, [t, discoverEvent, discoverPractitioner, discoverVenue]);

  const skeletonSlide = (i: number) => (
    <SwiperSlide key={i} className="!h-auto">
      <div className="animate-pulse pr-2">
        <div className="rounded-2xl overflow-hidden bg-[#E8EDE3] h-48" />
        <div className="pt-4 space-y-3">
          <div className="h-4 bg-[#E8EDE3] rounded w-1/3" />
          <div className="h-5 bg-[#E8EDE3] rounded w-5/6" />
          <div className="h-3 bg-[#E8EDE3] rounded w-2/3" />
        </div>
      </div>
    </SwiperSlide>
  );

  if (loading) {
    return (
      <>
        <div className="lg:hidden -mx-4 px-4 pb-16 md:pb-24">
          <Swiper
            slidesPerView={1.42}
            spaceBetween={16}
            modules={[Pagination]}
            pagination={{ clickable: true }}
            className="discover-home-swiper !pb-10"
          >
            {[0, 1, 2, 3].map((i) => skeletonSlide(i))}
          </Swiper>
        </div>
        <div className="hidden lg:grid lg:grid-cols-4 gap-6 lg:gap-8 pb-16 md:pb-24">
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className="animate-pulse">
              <div className="rounded-2xl overflow-hidden bg-[#E8EDE3] h-48" />
              <div className="pt-4 space-y-3">
                <div className="h-4 bg-[#E8EDE3] rounded w-1/3" />
                <div className="h-5 bg-[#E8EDE3] rounded w-5/6" />
                <div className="h-3 bg-[#E8EDE3] rounded w-2/3" />
              </div>
            </div>
          ))}
        </div>
      </>
    );
  }

  return (
    <>
      <div className="lg:hidden -mx-4 px-4 pb-16 md:pb-24">
        <Swiper
          slidesPerView={1.42}
          spaceBetween={16}
          modules={[Pagination]}
          pagination={{ clickable: true }}
          className="discover-home-swiper !pb-10"
        >
          {discoverCards.map((card, idx) => (
            <SwiperSlide
              key={isValidElement(card) && card.key != null ? String(card.key) : `discover-${idx}`}
              className="!h-auto"
            >
              <div className="pr-2 h-full">{card}</div>
            </SwiperSlide>
          ))}
        </Swiper>
      </div>
      <div className="hidden lg:grid lg:grid-cols-4 gap-6 lg:gap-8 pb-16 md:pb-24">{discoverCards}</div>
    </>
  );
}
